import { useEffect, useState } from "react";
import { Trophy, TrendingDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { BULAN, STATUS, fmt } from "@/lib/format";
import { useFilters } from "@/context/FilterContext";
import { StatusBadge } from "@/components/StatusBadge";

function Row({ r, total, onPick, showWeak }) {
  return (
    <li>
      <button data-testid={`leaderboard-row-${r.unit.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => onPick(r.unit)}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-muted/60">
        <span className="font-num grid h-8 w-8 shrink-0 place-items-center rounded-md bg-muted text-sm font-bold">{r.rank}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-semibold">{r.unit}</p>
            <span className="font-num text-sm font-bold">{fmt(r.score, "pct")}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div className={`h-full ${STATUS[r.status]?.bar}`} style={{ width: `${Math.min(r.score / 1.2, 1) * 100}%` }} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span>{r.tipe} · peringkat {r.rank}/{total}</span>
            {showWeak && <span className="rounded bg-rose-50 px-1.5 py-0.5 font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">Terlemah: {r.weakest.label} {fmt(r.weakest.value, "pct")}</span>}
          </div>
        </div>
      </button>
    </li>
  );
}

function Column({ title, icon: Icon, tone, rows, total, onPick, showWeak, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border bg-background/50 p-3">
      <p className={`mb-1 flex items-center gap-2 px-2 text-xs font-semibold uppercase tracking-wider ${tone}`}><Icon className="h-4 w-4" />{title}</p>
      {rows.length === 0 ? <p className="px-2 py-6 text-center text-sm text-muted-foreground">Tidak ada data.</p> : (
        <ul>{rows.map((r) => <Row key={r.unit} r={r} total={total} onPick={onPick} showWeak={showWeak} />)}</ul>
      )}
    </div>
  );
}

export const Leaderboard = () => {
  const { filters, setFilters, dataVersion } = useFilters();
  const [bulan, setBulan] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const p = { tahun: filters.tahun, uid: filters.uid, tipe: filters.tipe, bulan };
    Object.keys(p).forEach((k) => p[k] == null && delete p[k]);
    api.get("/leaderboard", { params: p }).then((r) => setData(r.data));
  }, [filters.tahun, filters.uid, filters.tipe, bulan, dataVersion]);

  if (!data || !data.months?.length) return null;
  const pick = (unit) => setFilters({ unit });

  return (
    <div data-testid="leaderboard" className="rounded-xl border bg-card p-4 sm:p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-heading text-base sm:text-lg font-semibold">Peringkat Unit — {data.label}</h3>
          <p className="text-xs text-muted-foreground">Skor gabungan berbobot: {(data.indicators || []).filter((i) => i.weight > 0).map((i) => `${i.label} ${Number(i.weight).toLocaleString("id-ID")}%`).join(", ")} (tiap indikator dibatasi 120%). Klik unit untuk detail.</p>
        </div>
        <Select value={String(data.bulan)} onValueChange={(v) => setBulan(Number(v))}>
          <SelectTrigger data-testid="leaderboard-month-select" className="h-9 w-40 shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>{data.months.map((m) => <SelectItem key={m} value={String(m)}>{BULAN[m - 1]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {data.scoped ? (
        <div className="mt-4"><Column testid="leaderboard-own" title="Posisi unit Anda" icon={Trophy} tone="text-[#0A3967] dark:text-[#F5A623]" rows={data.rows} total={data.total} onPick={pick} showWeak /></div>
      ) : (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Column testid="leaderboard-best" title="Terbaik" icon={Trophy} tone="text-emerald-600" rows={data.best} total={data.total} onPick={pick} />
          <Column testid="leaderboard-worst" title="Perlu Perhatian" icon={TrendingDown} tone="text-rose-600" rows={data.worst} total={data.total} onPick={pick} showWeak />
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
        {["green", "yellow", "red"].map((s) => <StatusBadge key={s} status={s} />)}
      </div>
    </div>
  );
};
